import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

const CORE_VARIANT_KEYS = ['monthly_essentials', 'monthly_support', 'yearly_essentials', 'yearly_support'];
const PLAN_KEYS = ['essentials', 'support'];
const BILLING_KEYS = ['monthly', 'yearly'];
const PACKAGE_GROUP = 'website-configurator';
const ANALYTICS_EVENT_PREFIX = 'bft';
const ANALYTICS_EVENTS = {
  started: `${ANALYTICS_EVENT_PREFIX}:configurator_started`,
  stepViewed: `${ANALYTICS_EVENT_PREFIX}:configurator_step_viewed`,
  stepCompleted: `${ANALYTICS_EVENT_PREFIX}:configurator_step_completed`,
  submitStarted: `${ANALYTICS_EVENT_PREFIX}:configurator_submit_started`,
  submitSuccess: `${ANALYTICS_EVENT_PREFIX}:configurator_submit_success`,
  submitError: `${ANALYTICS_EVENT_PREFIX}:configurator_submit_error`,
  planSelected: `${ANALYTICS_EVENT_PREFIX}:configurator_plan_selected`,
  billingSelected: `${ANALYTICS_EVENT_PREFIX}:configurator_billing_selected`,
  addOnToggled: `${ANALYTICS_EVENT_PREFIX}:configurator_addon_toggled`,
  replaceDialogShown: `${ANALYTICS_EVENT_PREFIX}:configurator_replace_dialog_shown`,
  replaceDialogConfirmed: `${ANALYTICS_EVENT_PREFIX}:configurator_replace_dialog_confirmed`,
};

const PLAN_DESCRIPTIONS = {
  essentials: 'A lean website package with hosting included.',
  support: 'A hands-off website package with ongoing support included.',
};

const BILLING_DESCRIPTIONS = {
  monthly: 'For tradies that prefer to spread the cost across 12 months while getting their website live sooner.',
  yearly: 'For tradies ready to pay upfront and save 25% on the first 12 months of their chosen plan.',
};

const CHECK_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M6.5 11L9.5 14L15.5 8M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ADDED_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none"><path d="M5.41671 9.16659L7.91671 11.6666L12.9167 6.66659M17.5 9.16659C17.5 13.769 13.7691 17.4999 9.16671 17.4999C4.56433 17.4999 0.833374 13.769 0.833374 9.16659C0.833374 4.56421 4.56433 0.833252 9.16671 0.833252C13.7691 0.833252 17.5 4.56421 17.5 9.16659Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ADD_TO_CART_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><g clip-path="url(#clip0_add_to_cart_icon)"><path d="M10.0001 6.66663V13.3333M6.66675 9.99996H13.3334M18.3334 9.99996C18.3334 14.6023 14.6025 18.3333 10.0001 18.3333C5.39771 18.3333 1.66675 14.6023 1.66675 9.99996C1.66675 5.39759 5.39771 1.66663 10.0001 1.66663C14.6025 1.66663 18.3334 5.39759 18.3334 9.99996Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_add_to_cart_icon"><rect width="20" height="20" fill="white"/></clipPath></defs></svg>';
const BACK_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M16.6667 10H3.33333M3.33333 10L8.33333 15M3.33333 10L8.33333 5" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const FORWARD_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.33325 10H16.6666M16.6666 10L11.6666 5M16.6666 10L11.6666 15" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[•|/_-]+/g, ' ')
    .replace(/[^a-z0-9\s+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeCadenceToken(value) {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) return null;
  if (normalizedValue.includes('month')) return 'monthly';
  if (normalizedValue.includes('annual')) return 'yearly';
  if (normalizedValue.includes('yearly')) return 'yearly';
  if (normalizedValue === 'year') return 'yearly';

  return null;
}

function normalizePlanToken(value) {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) return null;
  if (normalizedValue.includes('support')) return 'support';
  if (normalizedValue.includes('essential')) return 'essentials';
  if (normalizedValue.includes('website + hosting + support')) return 'support';
  if (normalizedValue.includes('website hosting support')) return 'support';
  if (normalizedValue.includes('website + hosting')) return 'essentials';
  if (normalizedValue.includes('website hosting')) return 'essentials';

  return null;
}

function normalizeHighlights(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n|[|•]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

   if (value && typeof value === 'object') {
    const richTextItems = [];

    const readNodeText = (node) => {
      if (!node || typeof node !== 'object') return '';

      if (node.type === 'text') {
        return String(node.value || '');
      }

      if (Array.isArray(node.children)) {
        return node.children.map(readNodeText).join('');
      }

      return '';
    };

    const walkNodes = (nodes) => {
      if (!Array.isArray(nodes)) return;

      nodes.forEach((node) => {
        if (!node || typeof node !== 'object') return;

        if (node.type === 'list-item') {
          const text = readNodeText(node).trim();
          if (text) richTextItems.push(text);
        }

        if (Array.isArray(node.children)) {
          walkNodes(node.children);
        }
      });
    };

    walkNodes(Array.isArray(value.children) ? value.children : []);

    if (richTextItems.length > 0) {
      return richTextItems;
    }
  }

  return [];
}

function normalizeMoneyToCents(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;

    // Shopify money metafields may already be serialized as minor units.
    // Treat integers as cents, and only scale decimal values.
    return Number.isInteger(value) ? value : Math.round(value * 100);
  }

  if (typeof value === 'string') {
    const normalizedValue = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    const parsedValue = Number.parseFloat(normalizedValue);
    if (!Number.isFinite(parsedValue)) return null;

    return /[.,]\d+$/.test(value) ? Math.round(parsedValue * 100) : Math.round(parsedValue);
  }

  if (typeof value === 'object') {
    if ('amount' in value) {
      return normalizeMoneyToCents(value.amount);
    }

    if ('value' in value) {
      return normalizeMoneyToCents(value.value);
    }
  }

  return null;
}

function parseSortOrder(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : Number.MAX_SAFE_INTEGER;
}

function extractServiceLabel(variantTitle) {
  const normalizedTitle = String(variantTitle || '')
    .replace(/monthly/gi, '')
    .replace(/yearly/gi, '')
    .replace(/annual/gi, '')
    .replace(/[•|]/g, ' ')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^\s*\/+\s*/, '')
    .trim();

  return normalizedTitle || 'Website Package';
}

function buildPlanDisplayTitle(title, billing) {
  return `${extractServiceLabel(title)} (${billing === 'yearly' ? 'Annual Plan' : 'Monthly Plan'})`;
}

function buildRichTextMarkup(html, trailingText = '') {
  const baseMarkup = typeof html === 'string' ? html.trim() : '';
  const suffixMarkup = trailingText ? escapeHtml(trailingText) : '';

  if (!baseMarkup) return suffixMarkup;
  if (!suffixMarkup) return baseMarkup;

  if (/<\/p>\s*$/i.test(baseMarkup)) {
    return baseMarkup.replace(/<\/p>\s*$/i, `${suffixMarkup}</p>`);
  }

  return `${baseMarkup}${suffixMarkup}`;
}

function formatDisplayedPrice(formatMoneyFn, cents, billing) {
  if (billing === 'monthly') {
    return `${formatMoneyFn(cents)}/month`;
  }

  return formatMoneyFn(cents);
}

function formatDisplayedRenewal(formatMoneyFn, cents, billing) {
  return `Renews at ${formatMoneyFn(cents)}/${billing === 'monthly' ? 'month' : 'year'}`;
}

function formatLineItemPrice(formatMoneyFn, cents, billing) {
  if (billing === 'monthly') {
    return `${formatMoneyFn(cents)}/month`;
  }

  return formatMoneyFn(cents);
}

function formatLineItemDetail(formatMoneyFn, priceCents, renewalCents, billing) {
  return `${formatLineItemPrice(formatMoneyFn, priceCents, billing)}, ${formatDisplayedRenewal(formatMoneyFn, renewalCents, billing)}`;
}

function createIconMarkup(svg, className = '') {
  const icon = document.createElement('span');
  if (className) icon.className = className;
  icon.innerHTML = svg;
  return icon;
}

function createButtonContent(label, svg = null, iconClassName = '', iconPosition = 'right') {
  const fragment = document.createDocumentFragment();
  const labelSpan = document.createElement('span');
  labelSpan.textContent = label;

  if (svg && iconPosition === 'left') {
    fragment.append(createIconMarkup(svg, iconClassName));
  }

  fragment.append(labelSpan);

  if (svg && iconPosition !== 'left') {
    fragment.append(createIconMarkup(svg, iconClassName));
  }

  return fragment;
}

function buildCoreKey(cadence, plan) {
  if (!cadence || !plan) return null;
  return `${cadence}_${plan}`;
}

function parseJsonScript(element, selector) {
  const script = element.querySelector(selector);

  if (!(script instanceof HTMLScriptElement)) {
    throw new Error(`Missing configurator data script: ${selector}`);
  }

  return JSON.parse(script.textContent || 'null');
}

function addWarning(warnings, message) {
  if (!message || warnings.includes(message)) return;
  warnings.push(message);
}

function resolveCoreVariantMap(coreProduct) {
  const optionMap = {};
  const optionErrors = [];

  for (const variant of coreProduct.variants || []) {
    const cadence = (variant.optionValues || []).map(normalizeCadenceToken).find(Boolean) || null;
    const plan = (variant.optionValues || []).map(normalizePlanToken).find(Boolean) || null;
    const key = buildCoreKey(cadence, plan);

    if (!key) continue;
    if (optionMap[key] && optionMap[key].id !== variant.id) {
      optionErrors.push(`Duplicate core option mapping for ${key}.`);
      continue;
    }

    optionMap[key] = variant;
  }

  const optionMapIsComplete = CORE_VARIANT_KEYS.every((key) => optionMap[key]);
  if (optionMapIsComplete && optionErrors.length === 0) {
    return { source: 'options', variantMap: optionMap, errors: [] };
  }

  const titleMap = {};
  const titleErrors = [];

  for (const variant of coreProduct.variants || []) {
    const cadence = normalizeCadenceToken(variant.title);
    const plan = normalizePlanToken(variant.title);
    const key = buildCoreKey(cadence, plan);

    if (!key) continue;
    if (titleMap[key] && titleMap[key].id !== variant.id) {
      titleErrors.push(`Duplicate core title mapping for ${key}.`);
      continue;
    }

    titleMap[key] = variant;
  }

  const titleMapIsComplete = CORE_VARIANT_KEYS.every((key) => titleMap[key]);
  if (titleMapIsComplete && titleErrors.length === 0) {
    return { source: 'title', variantMap: titleMap, errors: optionErrors };
  }

  return {
    source: null,
    variantMap: null,
    errors: [...optionErrors, ...titleErrors, 'Core product must resolve to monthly/yearly and essentials/support variants.'],
  };
}

function resolveAddOnVariantMap(addOnProduct) {
  const warnings = [];

  const optionMap = {};
  for (const variant of addOnProduct.variants || []) {
    const cadence = (variant.optionValues || []).map(normalizeCadenceToken).find(Boolean) || null;

    if (!cadence) continue;
    if (optionMap[cadence] && optionMap[cadence].id !== variant.id) {
      addWarning(warnings, `${addOnProduct.title} has duplicate option-based ${cadence} variants.`);
      continue;
    }

    optionMap[cadence] = variant;
  }

  const optionMapIsComplete = BILLING_KEYS.every((key) => optionMap[key]);
  if (optionMapIsComplete) {
    return { source: 'options', variantMap: optionMap, warnings };
  }

  const titleMap = {};
  for (const variant of addOnProduct.variants || []) {
    const cadence = normalizeCadenceToken(variant.title);

    if (!cadence) continue;
    if (titleMap[cadence] && titleMap[cadence].id !== variant.id) {
      addWarning(warnings, `${addOnProduct.title} has duplicate title-based ${cadence} variants.`);
      continue;
    }

    titleMap[cadence] = variant;
  }

  const titleMapIsComplete = BILLING_KEYS.every((key) => titleMap[key]);
  if (titleMapIsComplete) {
    return { source: 'title', variantMap: titleMap, warnings };
  }

  addWarning(warnings, `${addOnProduct.title} is missing a clean monthly/yearly add-on variant structure.`);

  return { source: null, variantMap: null, warnings };
}

function getLineItemKeyValue(properties, key) {
  if (!properties) return null;
  if (Array.isArray(properties)) {
    const entry = properties.find((property) => property?.name === key);
    return entry?.value ?? null;
  }

  return properties[key] ?? null;
}

class WebsitePackageConfiguratorComponent extends Component {
  #state = {
    currentStep: 1,
    selectedPlan: 'essentials',
    selectedBilling: 'monthly',
    selectedAddOnHandles: new Set(),
  };

  #coreProduct;
  #copy;
  #coreVariantMap;
  #normalizedAddOns = [];
  #editorWarnings = [];
  #fatalMessage = null;
  #isSubmitting = false;
  #isConfirmingReplacement = false;
  #isFocusMode = false;
  #isDesignMode = false;
  #isFocusModeEnabled = false;
  #configuratorSection = null;
  #focusModeSiblings = [];
  #boundHashChangeHandler = null;
  #replaceDialogConfirmButton = null;
  #replaceDialogCancelButton = null;
  #hasTrackedStart = false;
  #lastTrackedViewedStep = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#isDesignMode = this.dataset.designMode === 'true';
      this.#isFocusModeEnabled = this.dataset.focusModeEnabled === 'true';
      this.#initializeFocusModeContext();
      this.#coreProduct = this.#normalizeProductData(parseJsonScript(this, '[data-core-product]'));
      this.#copy = parseJsonScript(this, '[data-configurator-copy]');

      const addOnProducts = (parseJsonScript(this, '[data-add-on-products]') || []).map((addOnProduct) =>
        this.#normalizeProductData(addOnProduct)
      );
      this.#normalizedAddOns = this.#normalizeAddOns(addOnProducts);

      this.#hydrateStaticCopy();
      this.#hydrateProgress();
      this.#resolveCoreVariants();
      this.#setupBaseInteractions();
      this.#setupHashTrigger();

      if (this.#fatalMessage) {
        this.#showFatalValidation(this.#fatalMessage);
        this.#renderEditorWarnings();
        return;
      }

      this.#render();
      this.#trackStartedAndStepView();
      this.#renderEditorWarnings();
    } catch (error) {
      console.error(error);
      this.#showFatalValidation(this.#copy?.messages?.missingData || 'This configurator is missing required data.');
    }
  }

  disconnectedCallback() {
    if (this.#boundHashChangeHandler) {
      window.removeEventListener('hashchange', this.#boundHashChangeHandler);
      this.#boundHashChangeHandler = null;
    }

    super.disconnectedCallback?.();
  }

  #setupBaseInteractions() {
    for (const button of this.querySelectorAll('[data-plan-select]')) {
      button.addEventListener('click', () => {
        const plan = button.getAttribute('data-plan-select');
        if (!PLAN_KEYS.includes(plan || '')) return;

        this.#state.selectedPlan = plan;
        this.#publishAnalyticsEvent(ANALYTICS_EVENTS.planSelected, {
          selected_plan: plan,
        });
        this.#navigateToStep(2);
      });
    }

    for (const button of this.querySelectorAll('[data-billing-select]')) {
      button.addEventListener('click', () => {
        const billing = button.getAttribute('data-billing-select');
        if (!BILLING_KEYS.includes(billing || '')) return;

        this.#updateBilling(billing);
        this.#publishAnalyticsEvent(ANALYTICS_EVENTS.billingSelected, {
          selected_billing: billing,
        });
        this.#navigateToStep(3);
      });
    }

    for (const button of this.querySelectorAll('[data-nav-back]')) {
      button.replaceChildren(
        createButtonContent(this.#copy.labels.back, BACK_ICON_SVG, 'website-package-configurator__button-icon', 'left')
      );
      button.addEventListener('click', () => {
        const previousStep = Number(button.getAttribute('data-nav-back'));
        this.#navigateToStep(previousStep);
      });
    }

    this.refs.stepThreeCta.addEventListener('click', () => {
      this.#navigateToStep(4);
    });

    this.refs.stepThreeBottomCta.addEventListener('click', () => {
      this.#navigateToStep(4);
    });

    this.refs.changePlanButton.addEventListener('click', () => {
      this.#togglePlan();
      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.planSelected, {
        selected_plan: this.#state.selectedPlan,
      });
      this.#render();
    });

    this.refs.changeBillingButton.addEventListener('click', () => {
      const nextBilling = this.#state.selectedBilling === 'monthly' ? 'yearly' : 'monthly';
      this.#updateBilling(nextBilling);
      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.billingSelected, {
        selected_billing: this.#state.selectedBilling,
      });
      this.#render();
    });

    this.refs.backToProductButton.replaceChildren(
      createButtonContent(this.#copy.labels.backToProduct, BACK_ICON_SVG, 'website-package-configurator__button-icon', 'left')
    );
    this.refs.backToProductButton.addEventListener('click', () => {
      this.#exitFocusMode();
    });

    this.refs.addToCartButton.addEventListener('click', () => {
      this.#submitPackage();
    });

    const replaceDialog = this.refs.replaceDialog;
    const replaceDialogConfirm = replaceDialog?.querySelector('[ref="replaceDialogConfirm"]');
    const replaceDialogCancel = replaceDialog?.querySelector('[ref="replaceDialogCancel"]');
    const replaceDialogTitle = replaceDialog?.querySelector('[ref="replaceDialogTitle"]');
    const replaceDialogBody = replaceDialog?.querySelector('[ref="replaceDialogBody"]');
    this.#replaceDialogConfirmButton = replaceDialogConfirm instanceof HTMLButtonElement ? replaceDialogConfirm : null;
    this.#replaceDialogCancelButton = replaceDialogCancel instanceof HTMLButtonElement ? replaceDialogCancel : null;

    if (replaceDialogTitle) {
      replaceDialogTitle.textContent = this.#copy.messages.replaceDialogTitle;
    }
    if (replaceDialogBody) {
      replaceDialogBody.textContent = this.#copy.messages.replaceDialogBody;
    }
    if (replaceDialogCancel) {
      replaceDialogCancel.textContent = this.#copy.messages.replaceDialogCancel;
    }
    if (replaceDialogConfirm) {
      replaceDialogConfirm.textContent = this.#copy.messages.replaceDialogConfirm;
      replaceDialogConfirm.addEventListener('click', () => {
        this.#confirmReplacementAndSubmit();
      });
    }
  }

  #normalizeProductData(productData) {
    return {
      ...productData,
      variants: (productData.variants || []).map((variant) => ({
        ...variant,
        renewalPriceCents:
          variant.renewalPriceRaw != null && variant.renewalPriceRaw !== ''
            ? normalizeMoneyToCents(variant.renewalPriceRaw)
            : variant.renewalPriceCents != null
              ? Number(variant.renewalPriceCents)
              : null,
      })),
    };
  }

  #hydrateStaticCopy() {
    this.refs.heading.textContent = this.#copy.labels.heading;
    this.refs.stepOneNote.innerHTML = buildRichTextMarkup(this.#copy.stepNotes.step1);
    this.refs.stepTwoNote.innerHTML = buildRichTextMarkup(this.#copy.stepNotes.step2);
    this.refs.stepThreeNote.innerHTML = buildRichTextMarkup(this.#copy.stepNotes.step3);
    this.refs.reviewHeading.textContent = this.#copy.labels.reviewHeading;
    this.refs.reviewSupportingText.innerHTML = buildRichTextMarkup(this.#copy.labels.reviewSupportingText);
    this.refs.summaryLabel.textContent = this.#copy.labels.summaryLabel;
    this.refs.addToCartButton.textContent = this.#copy.labels.addToCart;
    this.refs.changePlanButton.textContent = this.#copy.labels.changePlan;
  }

  #hydrateProgress() {
    const progressSteps = Array.from(this.querySelectorAll('[data-progress-step]'));

    progressSteps.forEach((stepElement, index) => {
      const title = stepElement.querySelector('[data-progress-title]');
      const description = stepElement.querySelector('[data-progress-description]');
      const marker = stepElement.querySelector('[data-progress-marker]');
      const copy = this.#copy.messages.steps[index];

      if (title) title.textContent = copy.title;
      if (description) description.textContent = copy.description;
      if (marker) marker.textContent = String(index + 1);
    });
  }

  #resolveCoreVariants() {
    const resolution = resolveCoreVariantMap(this.#coreProduct);

    if (!resolution.variantMap) {
      this.#fatalMessage = this.#copy.messages.invalidCore;
      resolution.errors.forEach((warning) => addWarning(this.#editorWarnings, warning));
      return;
    }

    this.#coreVariantMap = resolution.variantMap;

    for (const key of CORE_VARIANT_KEYS) {
      const variant = this.#coreVariantMap[key];
      if (!variant || variant.renewalPriceCents == null) {
        this.#fatalMessage = this.#copy.messages.missingRenewal;
        addWarning(this.#editorWarnings, `${variant?.title || key} is missing a renewal price.`);
      }
    }
  }

  #normalizeAddOns(addOnProducts) {
    const normalizedAddOns = [];

    for (const addOnProduct of addOnProducts || []) {
      const resolution = resolveAddOnVariantMap(addOnProduct);
      resolution.warnings.forEach((warning) => addWarning(this.#editorWarnings, warning));

      if (!resolution.variantMap) {
        continue;
      }

      if (BILLING_KEYS.some((key) => resolution.variantMap[key].renewalPriceCents == null)) {
        addWarning(this.#editorWarnings, `${addOnProduct.title} is missing a renewal price and was excluded.`);
        continue;
      }

      normalizedAddOns.push({
        ...addOnProduct,
        highlights: normalizeHighlights(addOnProduct.shortKeyHighlights),
        variantMap: resolution.variantMap,
      });
    }

    const liquidWarnings = Array.from(this.querySelectorAll('.website-package-configurator__liquid-warning li')).map((item) =>
      item.textContent?.trim()
    );

    liquidWarnings.filter(Boolean).forEach((warning) => {
      addWarning(this.#editorWarnings, `${warning} is in the selected collection but does not have custom.product_role = addon.`);
    });

    normalizedAddOns.sort((left, right) => {
      const sortOrderDifference = parseSortOrder(left.sortOrder) - parseSortOrder(right.sortOrder);
      if (sortOrderDifference !== 0) return sortOrderDifference;
      return left.title.localeCompare(right.title);
    });

    return normalizedAddOns;
  }

  #initializeFocusModeContext() {
    if (!this.#isFocusModeEnabled) return;

    this.#configuratorSection = this.closest('.shopify-section');

    const mainContent = document.querySelector('#MainContent');
    if (!(mainContent instanceof HTMLElement) || !(this.#configuratorSection instanceof HTMLElement)) {
      return;
    }

    this.#focusModeSiblings = Array.from(mainContent.children).filter((element) => {
      return element instanceof HTMLElement && element.classList.contains('shopify-section') && element !== this.#configuratorSection;
    });
  }

  #goToStep(stepNumber) {
    const nextStep = Math.max(1, Math.min(4, Number(stepNumber) || 1));
    this.#state.currentStep = nextStep;

    if (!this.#isDesignMode && this.#isFocusModeEnabled && nextStep >= 2) {
      this.#setFocusMode(true);
    }

    return nextStep;
  }

  #navigateToStep(stepNumber, options = {}) {
    const previousStep = this.#state.currentStep;
    const nextStep = this.#goToStep(stepNumber);

    this.#render();
    this.#scrollToConfigurator();

    if (nextStep > previousStep) {
      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.stepCompleted, {
        step_number: previousStep,
        step_name: this.#stepName(previousStep),
        next_step_number: nextStep,
        next_step_name: this.#stepName(nextStep),
      });
    }

    this.#trackStartedAndStepView(options);
  }

  #setFocusMode(shouldFocus) {
    if (this.#isDesignMode || !this.#isFocusModeEnabled) return;

    this.#isFocusMode = shouldFocus;
    this.classList.toggle('is-focus-mode', shouldFocus);

    this.#focusModeSiblings.forEach((section) => {
      section.hidden = shouldFocus;
    });
  }

  #exitFocusMode() {
    this.#setFocusMode(false);
    this.#clearFocusModeHash();
    this.#render();
    this.#scrollToConfigurator();
  }

  #scrollToConfigurator() {
    const scrollTarget = this.#configuratorSection instanceof HTMLElement ? this.#configuratorSection : this;
    const headerOffset = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
    const scrollTop = Math.max(window.scrollY + scrollTarget.getBoundingClientRect().top - headerOffset - 16, 0);

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    });
  }

  #setupHashTrigger() {
    if (this.#isDesignMode || !this.#isFocusModeEnabled) return;

    this.#boundHashChangeHandler = () => {
      this.#handleHashTrigger();
    };

    window.addEventListener('hashchange', this.#boundHashChangeHandler);
  }

  #handleHashTrigger() {
    if (window.location.hash !== '#buy') return;

    this.#setFocusMode(true);
    this.#state.currentStep = 1;
    this.#render();
    this.#scrollToConfigurator();
    this.#trackStartedAndStepView({ entryMethod: 'hash_buy' });
  }

  #clearFocusModeHash() {
    if (window.location.hash !== '#buy') return;

    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`
    );
  }

  #updateBilling(nextBilling) {
    if (!BILLING_KEYS.includes(nextBilling)) return;

    const removedAddOns = [];
    const nextAddOnHandles = new Set();

    for (const addOnHandle of this.#state.selectedAddOnHandles) {
      const addOnProduct = this.#normalizedAddOns.find((candidate) => candidate.handle === addOnHandle);
      if (addOnProduct?.variantMap?.[nextBilling]) {
        nextAddOnHandles.add(addOnHandle);
      } else {
        removedAddOns.push(addOnProduct?.title || addOnHandle);
      }
    }

    this.#state.selectedBilling = nextBilling;
    this.#state.selectedAddOnHandles = nextAddOnHandles;

    removedAddOns.forEach((title) => {
      addWarning(this.#editorWarnings, `${title} was removed because it does not have a ${nextBilling} variant.`);
    });
  }

  #toggleAddOn(handle) {
    const addOnProduct = this.#normalizedAddOns.find((candidate) => candidate.handle === handle);
    if (!addOnProduct || !addOnProduct.variantMap[this.#state.selectedBilling]) return;

    const wasSelected = this.#state.selectedAddOnHandles.has(handle);

    if (this.#state.selectedAddOnHandles.has(handle)) {
      this.#state.selectedAddOnHandles.delete(handle);
    } else {
      this.#state.selectedAddOnHandles.add(handle);
    }

    this.#publishAnalyticsEvent(ANALYTICS_EVENTS.addOnToggled, {
      add_on_handle: handle,
      add_on_name: addOnProduct.title,
      add_on_selected: !wasSelected,
    });

    this.#render();
  }

  #selectedCoreVariant() {
    return this.#coreVariantMap?.[buildCoreKey(this.#state.selectedBilling, this.#state.selectedPlan)] || null;
  }

  #selectedAddOnProducts() {
    return this.#normalizedAddOns.filter((addOnProduct) => this.#state.selectedAddOnHandles.has(addOnProduct.handle));
  }

  #selectedAddOnVariants() {
    return this.#selectedAddOnProducts()
      .map((addOnProduct) => ({
        product: addOnProduct,
        variant: addOnProduct.variantMap[this.#state.selectedBilling],
      }))
      .filter((entry) => entry.variant);
  }

  #calculateTotals() {
    const selectedCoreVariant = this.#selectedCoreVariant();
    const selectedAddOnVariants = this.#selectedAddOnVariants();

    if (!selectedCoreVariant) {
      return {
        initialTotalCents: 0,
        renewalTotalCents: 0,
      };
    }

    const initialTotalCents = selectedAddOnVariants.reduce(
      (total, entry) => total + Number(entry.variant.priceCents || 0),
      Number(selectedCoreVariant.priceCents || 0)
    );

    const renewalTotalCents = selectedAddOnVariants.reduce(
      (total, entry) => total + Number(entry.variant.renewalPriceCents || 0),
      Number(selectedCoreVariant.renewalPriceCents || 0)
    );

    return {
      initialTotalCents,
      renewalTotalCents,
    };
  }

  #moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  #currencyCode() {
    return this.dataset.currencyCode || 'AUD';
  }

  #formatMoney(cents) {
    return formatMoney(Number(cents || 0), this.#moneyFormat(), this.#currencyCode());
  }

  #render() {
    if (this.#fatalMessage) {
      this.#setFocusMode(false);
      this.#showFatalValidation(this.#fatalMessage);
      return;
    }

    this.refs.fatalValidation.hidden = true;
    this.querySelector('.website-package-configurator__progress')?.removeAttribute('hidden');
    this.querySelector('.website-package-configurator__steps')?.removeAttribute('hidden');

    this.#renderProgressState();
    this.#renderStepVisibility();
    this.#renderPlanCards();
    this.#renderBillingCards();
    this.#renderAddOnGrid();
    this.#renderSummaryBar();
    this.#renderReview();
    this.#renderFocusModeUi();
    this.#renderEditorWarnings();
  }

  #renderFocusModeUi() {
    const shouldShowBackToProduct = this.#isFocusMode && this.#state.currentStep === 1;

    this.refs.backToProductButton.hidden = !shouldShowBackToProduct;
    this.refs.backToProductButton.style.display = shouldShowBackToProduct ? 'inline-flex' : 'none';
  }

  #renderProgressState() {
    for (const stepElement of this.querySelectorAll('[data-progress-step]')) {
      const stepNumber = Number(stepElement.getAttribute('data-progress-step'));
      const marker = stepElement.querySelector('[data-progress-marker]');
      stepElement.classList.remove('is-current', 'is-complete');

      if (stepNumber < this.#state.currentStep) {
        stepElement.classList.add('is-complete');
        if (marker) marker.textContent = '✓';
      } else if (stepNumber === this.#state.currentStep) {
        stepElement.classList.add('is-current');
        if (marker) marker.textContent = '•';
      } else if (marker) {
        marker.textContent = String(stepNumber);
      }
    }
  }

  #renderStepVisibility() {
    const currentStep = this.#state.currentStep;

    (this.refs.stepPanels || []).forEach((panel) => {
      const stepNumber = Number(panel.getAttribute('data-step'));
      panel.classList.toggle('is-active', stepNumber === currentStep);
    });
  }

  #renderPlanCards() {
    for (const card of this.querySelectorAll('[data-plan-card]')) {
      const plan = card.getAttribute('data-plan-card');
      if (!PLAN_KEYS.includes(plan || '')) continue;

      const monthlyVariant = this.#coreVariantMap[`monthly_${plan}`];
      const yearlyVariant = this.#coreVariantMap[`yearly_${plan}`];
      const planLabel = plan === 'support' ? this.#copy.labels.planSupport : this.#copy.labels.planEssentials;
      const bannerLabel = this.#copy.bannerLabels?.[plan] || `THE ${planLabel.toUpperCase()} PLAN`;
      const serviceLabel = this.#copy.cardTitles?.[plan] || extractServiceLabel(monthlyVariant?.title || yearlyVariant?.title);

      card.querySelector('[data-plan-banner]').textContent = bannerLabel;
      card.querySelector('[data-plan-title]').textContent = serviceLabel;
      card.querySelector('[data-plan-description]').textContent = this.#copy.descriptions?.[plan] || PLAN_DESCRIPTIONS[plan];
      card.querySelector('[data-plan-monthly-price]').textContent = `${this.#formatMoney(monthlyVariant?.priceCents)}${this.#copy.labels.billingMonthly === 'Monthly' ? '/month' : ''}`;
      card.querySelector('[data-plan-annual-price]').textContent = `or ${this.#formatMoney(yearlyVariant?.priceCents)}`;
      card.querySelector('[data-plan-save-badge]').textContent = this.#copy.labels.save25Badge;
      card.querySelector('[data-plan-footer-note]').textContent = this.#copy.stepNotes.planCardFooter;

      const checklist = card.querySelector('[data-plan-checklist]');
      checklist.replaceChildren(
        ...(this.#copy.planChecklists[plan] || []).filter(Boolean).map((item) => this.#buildChecklistItem(item))
      );

      const button = card.querySelector('[data-plan-select]');
      button.replaceChildren(
        createButtonContent(this.#copy.labels.selectPlan, FORWARD_ICON_SVG, 'website-package-configurator__button-icon')
      );
    }
  }

  #renderBillingCards() {
    for (const card of this.querySelectorAll('[data-billing-card]')) {
      const billing = card.getAttribute('data-billing-card');
      if (!BILLING_KEYS.includes(billing || '')) continue;

      const variant = this.#coreVariantMap[buildCoreKey(billing, this.#state.selectedPlan)];
      const title = billing === 'monthly' ? `${this.#copy.labels.billingMonthly} Plan` : `${this.#copy.labels.billingYearly} Plan`;

      card.querySelector('[data-billing-banner]').textContent =
        billing === 'monthly' ? 'PAY OVER 12 MONTHS' : this.#copy.labels.billingSaveBadge.toUpperCase();
      card.querySelector('[data-billing-title]').textContent = title;
      card.querySelector('[data-billing-description]').textContent =
        this.#copy.descriptions?.[billing] || BILLING_DESCRIPTIONS[billing];
      card.querySelector('[data-billing-price]').textContent =
        billing === 'monthly'
          ? `${this.#formatMoney(variant?.priceCents)}/month`
          : `${this.#formatMoney(variant?.priceCents)}`;
      card.querySelector('[data-billing-renewal]').textContent =
        billing === 'monthly'
          ? `Renews at ${this.#formatMoney(variant?.renewalPriceCents)}/month`
          : `Renews at ${this.#formatMoney(variant?.renewalPriceCents)}/year`;

      const checklist = card.querySelector('[data-billing-checklist]');
      checklist.replaceChildren(
        ...(this.#copy.billingChecklists[billing] || []).filter(Boolean).map((item) => this.#buildChecklistItem(item))
      );

      const button = card.querySelector('[data-billing-select]');
      button.replaceChildren(
        createButtonContent(
          billing === 'monthly' ? this.#copy.labels.selectMonthly : this.#copy.labels.selectYearly,
          FORWARD_ICON_SVG,
          'website-package-configurator__button-icon'
        )
      );
    }
  }

  #renderAddOnGrid() {
    this.refs.addOnGrid.replaceChildren();

    if (this.#normalizedAddOns.length === 0) {
      const emptyState = this.refs.addOnEmptyState;
      emptyState.hidden = false;
      emptyState.textContent = this.#copy.messages.noAddOns;
      return;
    }

    this.refs.addOnEmptyState.hidden = true;

    for (const addOnProduct of this.#normalizedAddOns) {
      const variant = addOnProduct.variantMap[this.#state.selectedBilling];
      if (!variant) continue;

      const fragment = this.refs.addOnCardTemplate.content.cloneNode(true);
      const card = fragment.querySelector('.website-package-configurator__add-on-card');
      const title = fragment.querySelector('[data-add-on-title]');
      const description = fragment.querySelector('[data-add-on-description]');
      const price = fragment.querySelector('[data-add-on-price]');
      const highlights = fragment.querySelector('[data-add-on-highlights]');
      const button = fragment.querySelector('[data-add-on-toggle]');
      const isSelected = this.#state.selectedAddOnHandles.has(addOnProduct.handle);

      card.dataset.addOnHandle = addOnProduct.handle;
      card.classList.toggle('is-selected', isSelected);
      title.textContent = addOnProduct.title;
      description.textContent = addOnProduct.shortDescription || this.#copy.messages.noAddOns;
      price.textContent = formatDisplayedPrice(this.#formatMoney.bind(this), variant.priceCents, this.#state.selectedBilling);
      fragment.querySelector('[data-add-on-renewal]').textContent =
        formatDisplayedRenewal(this.#formatMoney.bind(this), variant.renewalPriceCents, this.#state.selectedBilling);

      const highlightItems = addOnProduct.highlights.length > 0 ? addOnProduct.highlights : [addOnProduct.scopeLabel || addOnProduct.scopeCode].filter(Boolean);
      highlights.replaceChildren(...highlightItems.map((item) => this.#buildChecklistItem(item)));

      button.replaceChildren(
        createButtonContent(
          isSelected ? this.#copy.labels.added : this.#copy.labels.addToPlan,
          isSelected ? ADDED_ICON_SVG : ADD_TO_CART_ICON_SVG,
          'website-package-configurator__button-icon'
        )
      );
      button.classList.toggle('is-added', isSelected);
      button.addEventListener('click', () => this.#toggleAddOn(addOnProduct.handle));

      this.refs.addOnGrid.append(fragment);
    }
  }

  #renderSummaryBar() {
    const coreVariant = this.#selectedCoreVariant();
    const selectedAddOnCount = this.#state.selectedAddOnHandles.size;
    const totals = this.#calculateTotals();
    const addOnLabel = selectedAddOnCount === 1 ? '1 add-on' : `${selectedAddOnCount} add-ons`;
    const planTitle = extractServiceLabel(coreVariant?.title || this.#coreProduct.title);

    this.refs.summaryTitle.textContent = `${planTitle} • ${addOnLabel}`;
    this.refs.summaryTotal.textContent =
      `${this.#copy.labels.reviewTotalPrefix} ${formatDisplayedPrice(this.#formatMoney.bind(this), totals.initialTotalCents, this.#state.selectedBilling)}`;
    this.refs.summaryRenewal.textContent =
      formatDisplayedRenewal(this.#formatMoney.bind(this), totals.renewalTotalCents, this.#state.selectedBilling);
    const ctaLabel = selectedAddOnCount === 0 ? this.#copy.labels.skip : this.#copy.labels.next;
    this.refs.stepThreeCta.replaceChildren(
      createButtonContent(ctaLabel, FORWARD_ICON_SVG, 'website-package-configurator__button-icon')
    );
    this.refs.stepThreeBottomCta.replaceChildren(
      createButtonContent(ctaLabel, FORWARD_ICON_SVG, 'website-package-configurator__button-icon')
    );
  }

  #renderReview() {
    const selectedCoreVariant = this.#selectedCoreVariant();
    const selectedAddOnVariants = this.#selectedAddOnVariants();
    const totals = this.#calculateTotals();
    const alternativeBilling = this.#state.selectedBilling === 'monthly' ? 'yearly' : 'monthly';
    const alternativeCoreVariant = this.#coreVariantMap[buildCoreKey(alternativeBilling, this.#state.selectedPlan)];
    const alternativeTotal = this.#selectedAddOnProducts().reduce((total, addOnProduct) => {
      return total + Number(addOnProduct.variantMap[alternativeBilling]?.priceCents || 0);
    }, Number(alternativeCoreVariant?.priceCents || 0));

    this.refs.reviewPackageIcon.replaceChildren(createIconMarkup(ADDED_ICON_SVG, 'website-package-configurator__review-package-icon-mark'));
    this.refs.reviewPackageTitle.textContent = buildPlanDisplayTitle(selectedCoreVariant.title, this.#state.selectedBilling);
    this.refs.reviewPackageTotal.textContent =
      `${this.#copy.labels.reviewTotalPrefix} ${formatDisplayedPrice(this.#formatMoney.bind(this), totals.initialTotalCents, this.#state.selectedBilling)}`;
    this.refs.reviewPackageRenewal.textContent =
      formatDisplayedRenewal(this.#formatMoney.bind(this), totals.renewalTotalCents, this.#state.selectedBilling);

    const reviewItems = [
      `${this.#state.selectedBilling === 'monthly' ? 'Monthly' : 'Annual'} / ${extractServiceLabel(selectedCoreVariant.title)} (${formatLineItemDetail(this.#formatMoney.bind(this), selectedCoreVariant.priceCents, selectedCoreVariant.renewalPriceCents, this.#state.selectedBilling)})`,
      ...selectedAddOnVariants.map(({ product, variant }) =>
        `${product.title} (${formatLineItemDetail(this.#formatMoney.bind(this), variant.priceCents, variant.renewalPriceCents, this.#state.selectedBilling)})`
      ),
    ];

    if (selectedAddOnVariants.length === 0) {
      reviewItems.push(this.#copy.labels.packageEmpty);
    }

    this.refs.reviewList.replaceChildren(...reviewItems.map((item) => this.#buildReviewItem(item)));
    this.refs.addToCartButton.replaceChildren(
      createButtonContent(this.#copy.labels.addToCart, ADD_TO_CART_ICON_SVG, 'website-package-configurator__button-icon')
    );
    this.refs.changePlanButton.textContent = this.#copy.labels.changePlan;
    this.refs.changeBillingButton.textContent =
      this.#state.selectedBilling === 'monthly' ? 'Change to Annual' : 'Change to Monthly';
    this.refs.reviewBillingNote.textContent =
      alternativeTotal > 0
        ? this.#state.selectedBilling === 'monthly'
          ? `Save 25% with Annual (${this.#formatMoney(alternativeTotal)})`
          : `Looking for Flexibility? Pay ${this.#formatMoney(alternativeTotal)}/month instead`
        : '';
    this.refs.stepFourNote.innerHTML = buildRichTextMarkup(
      this.#copy.stepNotes.step4Prefix,
      this.#state.selectedBilling === 'monthly'
        ? `${this.#formatMoney(totals.renewalTotalCents)}/month.`
        : `${this.#formatMoney(totals.renewalTotalCents)}/year.`
    );
  }

  #togglePlan() {
    this.#state.selectedPlan = this.#state.selectedPlan === 'essentials' ? 'support' : 'essentials';
  }

  #stepName(stepNumber) {
    return this.#copy?.messages?.steps?.[Number(stepNumber) - 1]?.title || `Step ${stepNumber}`;
  }

  #entryMethod(defaultEntryMethod = 'page_load') {
    if (window.location.hash === '#buy') return 'hash_buy';
    return defaultEntryMethod;
  }

  #totalsForAnalytics() {
    const totals = this.#calculateTotals();
    const currentValue = Number((totals.initialTotalCents || 0) / 100);

    return {
      value: currentValue,
      annualized_value: this.#state.selectedBilling === 'monthly' ? currentValue * 12 : currentValue,
      renewal_value: Number((totals.renewalTotalCents || 0) / 100),
    };
  }

  #baseAnalyticsPayload(overrides = {}) {
    const { value, annualized_value, renewal_value } = this.#totalsForAnalytics();

    return {
      product_id: String(this.#coreProduct?.productId || ''),
      product_handle: this.#coreProduct?.handle || '',
      step_number: this.#state.currentStep,
      step_name: this.#stepName(this.#state.currentStep),
      selected_plan: this.#state.selectedPlan,
      selected_billing: this.#state.selectedBilling,
      add_on_count: this.#state.selectedAddOnHandles.size,
      add_on_handles: Array.from(this.#state.selectedAddOnHandles),
      currency: this.#currencyCode(),
      value,
      annualized_value,
      renewal_value,
      ...overrides,
    };
  }

  #publishAnalyticsEvent(eventName, payload = {}) {
    if (this.#isDesignMode) return;

    const analytics = window.Shopify?.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return;

    try {
      analytics.publish(eventName, this.#baseAnalyticsPayload(payload));
    } catch (error) {
      console.error('Failed to publish configurator analytics event.', error);
    }
  }

  #trackStartedAndStepView(options = {}) {
    const entryMethod = this.#entryMethod(options.entryMethod);

    if (!this.#hasTrackedStart) {
      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.started, {
        entry_method: entryMethod,
      });
      this.#hasTrackedStart = true;
    }

    if (this.#lastTrackedViewedStep === this.#state.currentStep) return;

    this.#publishAnalyticsEvent(ANALYTICS_EVENTS.stepViewed, {
      entry_method: entryMethod,
    });
    this.#lastTrackedViewedStep = this.#state.currentStep;
  }

  #buildChecklistItem(text) {
    const item = document.createElement('li');
    item.className = 'website-package-configurator__checklist-item';

    const icon = document.createElement('span');
    icon.className = 'website-package-configurator__check-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.append(createIconMarkup(CHECK_ICON_SVG));

    const content = document.createElement('span');
    content.textContent = text;

    item.append(icon, content);
    return item;
  }

  #buildReviewItem(text) {
    const item = document.createElement('li');
    item.textContent = text;
    return item;
  }

  #showFatalValidation(message) {
    this.refs.fatalValidation.hidden = false;
    this.refs.fatalValidationTitle.textContent = this.#copy?.messages?.fatalValidationTitle || 'Configurator unavailable';
    this.refs.fatalValidationMessage.textContent = message;
    this.querySelector('.website-package-configurator__progress')?.setAttribute('hidden', '');
    this.querySelector('.website-package-configurator__steps')?.setAttribute('hidden', '');
  }

  #renderEditorWarnings() {
    const isDesignMode = this.dataset.designMode === 'true';

    if (!isDesignMode || this.#editorWarnings.length === 0) {
      this.refs.editorWarnings.hidden = true;
      return;
    }

    this.refs.editorWarnings.hidden = false;
    this.refs.editorWarningsList.replaceChildren(...this.#editorWarnings.map((warning) => this.#buildReviewItem(warning)));
  }

  async #submitPackage() {
    if (this.#isSubmitting || this.#isConfirmingReplacement) return;

    this.#publishAnalyticsEvent(ANALYTICS_EVENTS.submitStarted);
    this.#setAddToCartSubmitting(true);

    try {
      const selectedCoreVariant = this.#selectedCoreVariant();
      const totals = this.#calculateTotals();

      if (!selectedCoreVariant || totals.renewalTotalCents <= 0) {
        this.#publishAnalyticsEvent(ANALYTICS_EVENTS.submitError, {
          error_type: 'missing_renewal',
          error_message: this.#copy.messages.missingRenewal,
        });
        this.#showFatalValidation(this.#copy.messages.missingRenewal);
        this.#setAddToCartSubmitting(false);
        return;
      }

      const existingPackageLines = await this.#fetchExistingPackageLines();

      if (existingPackageLines.length > 0) {
        this.#setAddToCartSubmitting(false);
        this.#publishAnalyticsEvent(ANALYTICS_EVENTS.replaceDialogShown, {
          existing_package_count: existingPackageLines.length,
        });
        this.refs.replaceDialog.showDialog();
        return;
      }

      await this.#replaceAndAddPackage([]);
    } catch (error) {
      console.error(error);
      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.submitError, {
        error_type: 'submit_exception',
        error_message: error instanceof Error ? error.message : String(error),
      });
      this.refs.liveRegion.textContent = this.#copy.messages.cartError;
      this.#setAddToCartSubmitting(false);
    }
  }

  async #confirmReplacementAndSubmit() {
    if (this.#isSubmitting || this.#isConfirmingReplacement) return;

    this.#publishAnalyticsEvent(ANALYTICS_EVENTS.replaceDialogConfirmed);
    this.#setReplacementDialogSubmitting(true);

    try {
      const existingPackageLines = await this.#fetchExistingPackageLines();
      await this.#replaceAndAddPackage(existingPackageLines);
      this.refs.replaceDialog.closeDialog();
    } catch (error) {
      console.error(error);
      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.submitError, {
        error_type: 'replace_exception',
        error_message: error instanceof Error ? error.message : String(error),
      });
      this.refs.liveRegion.textContent = this.#copy.messages.cartError;
    } finally {
      this.#setReplacementDialogSubmitting(false);
    }
  }

  async #fetchExistingPackageLines() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    const cart = await response.json();

    return (cart.items || []).filter((item) => {
      return getLineItemKeyValue(item.properties, '_bft_package_group') === PACKAGE_GROUP;
    });
  }

  async #replaceAndAddPackage(existingPackageLines) {
    this.#isSubmitting = true;
    this.#setAddToCartSubmitting(true);

    try {
      if (existingPackageLines.length > 0) {
        const updates = {};

        existingPackageLines.forEach((lineItem) => {
          updates[lineItem.key] = 0;
        });

        await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ updates }) }));
      }

      const items = this.#buildCartItems();
      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items }),
        })
      );

      const addResult = await addResponse.json();
      if (addResult.status && addResult.status >= 400) {
        throw new Error(addResult.description || this.#copy.messages.cartError);
      }

      const updatedCartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const updatedCart = await updatedCartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(updatedCart, this.dataset.sectionId, {
          source: 'website-package-configurator',
          itemCount: updatedCart.item_count,
        })
      );

      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.submitSuccess, {
        cart_item_count: updatedCart.item_count,
        added_item_count: items.length,
      });
      this.refs.liveRegion.textContent = `${items.length} item${items.length === 1 ? '' : 's'} added to cart.`;
    } catch (error) {
      console.error(error);
      this.#publishAnalyticsEvent(ANALYTICS_EVENTS.submitError, {
        error_type: 'cart_error',
        error_message: error instanceof Error ? error.message : String(error),
      });
      this.refs.liveRegion.textContent = this.#copy.messages.cartError;
    } finally {
      this.#isSubmitting = false;
      this.#setAddToCartSubmitting(false);
    }
  }

  #buildCartItems() {
    const selectedCoreVariant = this.#selectedCoreVariant();
    const selectedAddOnVariants = this.#selectedAddOnVariants();

    return [
      {
        id: selectedCoreVariant.id,
        quantity: 1,
        properties: {
          _bft_package_group: PACKAGE_GROUP,
          _bft_package_role: 'core',
          _bft_billing: this.#state.selectedBilling,
          _bft_core_handle: this.#coreProduct.handle,
          _bft_scope_code: this.#coreProduct.scopeCode || '',
        },
      },
      ...selectedAddOnVariants.map(({ product, variant }) => ({
        id: variant.id,
        quantity: 1,
        properties: {
          _bft_package_group: PACKAGE_GROUP,
          _bft_package_role: 'addon',
          _bft_billing: this.#state.selectedBilling,
          _bft_core_handle: this.#coreProduct.handle,
          _bft_scope_code: product.scopeCode || '',
        },
      })),
    ];
  }

  #setReplacementDialogSubmitting(isSubmitting) {
    this.#isConfirmingReplacement = isSubmitting;

    if (this.#replaceDialogConfirmButton) {
      this.#replaceDialogConfirmButton.textContent = isSubmitting ? 'Adding...' : this.#copy.messages.replaceDialogConfirm;
      this.#replaceDialogConfirmButton.disabled = isSubmitting;
      this.#replaceDialogConfirmButton.setAttribute('aria-disabled', String(isSubmitting));
    }

    if (this.#replaceDialogCancelButton) {
      this.#replaceDialogCancelButton.disabled = isSubmitting;
      this.#replaceDialogCancelButton.setAttribute('aria-disabled', String(isSubmitting));
    }
  }

  #setAddToCartSubmitting(isSubmitting) {
    this.refs.addToCartButton.disabled = isSubmitting;
    this.refs.addToCartButton.setAttribute('aria-disabled', String(isSubmitting));

    if (isSubmitting) {
      this.refs.addToCartButton.replaceChildren(document.createTextNode('Adding...'));
      return;
    }

    this.refs.addToCartButton.replaceChildren(
      createButtonContent(this.#copy.labels.addToCart, ADD_TO_CART_ICON_SVG, 'website-package-configurator__button-icon')
    );
  }
}

if (!customElements.get('website-package-configurator-component')) {
  customElements.define('website-package-configurator-component', WebsitePackageConfiguratorComponent);
}
